export type ConversationFact = {
    key: string;
    label: string;
    value: string;
    source: string;
    verified: boolean | null;
};

export type ConversationContext = {
    attention: 'review' | 'handoff' | null;
    reason: { text: string; source: 'ai' | 'handoff' } | null;
    missingFacts: string[];
    facts: ConversationFact[];
    factsAvailability: 'available' | 'partial' | 'unavailable';
};

const FACT_LABELS: Record<string, string> = {
    need: 'Потребность',
    deadline: 'Срок',
    budget: 'Бюджет',
    lead_volume: 'Объём лидов',
    leads_per_month: 'Лидов в месяц',
    lead_source: 'Источник лидов',
    company: 'Компания',
    email: 'Почта',
    phone: 'Телефон',
    name: 'Имя',
    location: 'География',
    city: 'Город',
    service: 'Услуга',
    website: 'Сайт',
};
const SOURCES: Record<string, string> = {
    lead_snapshot: 'Из исходного запроса',
    llm_extraction: 'Извлечено ИИ',
    human_override: 'Добавлено вручную',
    integration: 'Из интеграции',
};

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function fieldLabel(key: string): string {
    if (Object.prototype.hasOwnProperty.call(FACT_LABELS, key)) return FACT_LABELS[key];
    // Custom Russian names are meaningful; generated IDs are not user-facing labels.
    return /[а-яё]/i.test(key) ? key.replace(/_/g, ' ') : 'Дополнительное поле';
}

function displayValue(value: unknown): string | null {
    if (typeof value === 'string') return value.trim() || null;
    if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (Array.isArray(value) && value.length && value.every((item) => !item || typeof item !== 'object')) {
        const parts = value.map(displayValue);
        return parts.every((part) => part !== null) ? parts.join(', ') : null;
    }
    return null;
}

/** Optional context must never make an otherwise valid transcript unreadable. */
export function normalizeConversationContext(value: unknown): ConversationContext {
    const raw = record(value);
    const signal = record(raw.latest_signal);
    const decision = record(raw.stage_decision);
    const qualification = record(raw.qualification_progress);
    const handoff = record(raw.handoff);
    const attention =
        raw.status === 'human_review_required' ? 'review' : raw.status === 'human_handoff' ? 'handoff' : null;
    const note = attention === 'handoff' ? text(handoff.note) : '';
    const summary = text(signal.reasoning_summary) || text(decision.reason);
    const reason: ConversationContext['reason'] = note
        ? { text: note, source: 'handoff' }
        : summary
        ? { text: summary, source: 'ai' }
        : null;
    // Current required fields take precedence over a previous model decision, even with an empty list.
    const missing = Array.isArray(qualification.missing_facts)
        ? qualification.missing_facts
        : Array.isArray(signal.missing_facts)
        ? signal.missing_facts
        : Array.isArray(decision.missing_facts)
        ? decision.missing_facts
        : [];
    const missingFacts = Array.from(new Set(missing.map(text).filter(Boolean).map(fieldLabel)));
    const facts: ConversationFact[] = [];
    let factsAvailability: ConversationContext['factsAvailability'] = Array.isArray(raw.facts)
        ? 'available'
        : 'unavailable';
    if (Array.isArray(raw.facts)) {
        const seen = new Set<string>();
        raw.facts.forEach((item) => {
            const fact = record(item);
            const key = text(fact.fact_key);
            const factValue =
                displayValue(fact.display_value) ?? displayValue(fact.value_text) ?? displayValue(fact.value_json);
            if (!key || factValue === null || seen.has(key)) {
                factsAvailability = 'partial';
                return;
            }
            seen.add(key);
            const source = text(fact.source);
            facts.push({
                key,
                label: fieldLabel(key),
                value: factValue,
                source: Object.prototype.hasOwnProperty.call(SOURCES, source) ? SOURCES[source] : 'Источник не указан',
                verified: typeof fact.is_verified === 'boolean' ? fact.is_verified : null,
            });
        });
    }
    return { attention, reason, missingFacts, facts, factsAvailability };
}
